import React, { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRightIcon, Building2Icon, CheckIcon, DropletsIcon, MessageSquareIcon, SproutIcon, WrenchIcon, BoxIcon, LightbulbIcon } from "lucide-react";
import { SITE } from "../constants/site";
type Service = {
  icon: BoxIcon;
  title: string;
  label: string;
  description: string;
  audience: string;
  highlights: string[];
};
const SERVICES: Service[] = [{
  icon: WrenchIcon,
  label: '01 / Repair',
  title: 'Sprinkler Repair & Diagnostics',
  description: 'Broken heads, clogged nozzles, pressure problems, leaks — we find the cause, not just the symptom.',
  audience: 'For systems that need answers before they need another quick fix.',
  highlights: ['Leak detection', 'Pressure correction', 'Head & nozzle repair']
}, {
  icon: DropletsIcon,
  label: '02 / Install',
  title: 'New Irrigation System Installation',
  description: 'Custom-designed irrigation for your property, not a cookie-cutter layout copied from the last job.',
  audience: 'Residential and commercial systems engineered for even, dependable coverage.',
  highlights: ['Custom zone design', 'Code-compliant install', 'Residential & commercial']
}, {
  icon: SproutIcon,
  label: '03 / Lawn',
  title: 'Sod & Grass Installation',
  description: 'New sod needs the right irrigation beneath it. We coordinate both for a lawn built to take root and thrive.',
  audience: 'One team for the system underneath and the lawn you see every day.',
  highlights: ['Sod installation', 'Irrigation coordination', 'Coverage planning']
}, {
  icon: Building2Icon,
  label: '04 / Commercial',
  title: 'Commercial Irrigation Services',
  description: 'Licensed oversight, documentation, and reliable zone management for high-visibility commercial properties.',
  audience: 'Built for property managers, HOAs, retail centers, and office parks.',
  highlights: ['HOA & property management', 'System documentation', 'Licensed oversight']
}, {
  icon: LightbulbIcon,
  label: '05 / Holiday',
  title: 'Christmas Light Installation',
  description: 'Professional, custom-fit holiday lighting for your home or business. We handle the design, installation, maintenance, and takedown.',
  audience: 'For property owners who want a flawless, hassle-free holiday display.',
  highlights: ['Custom design & fit', 'Commercial-grade lights', 'Takedown included']
}];
export function ServicesSection({
  onOpenSchedule
}: {
  onOpenSchedule?: () => void;
}) {
  const [selectedService, setSelectedService] = useState(0);
  const shouldReduceMotion = useReducedMotion();
  const activeService = SERVICES[selectedService];
  const ActiveIcon = activeService.icon;
  return <section id="services" className="w-full bg-slate-50 py-16 sm:py-24" data-id="element-478" data-mp="3eaniphlfrsw1">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-479" data-mp="olbsl0ogt0x6">
        <div className="mx-auto max-w-3xl text-center" data-id="element-480" data-mp="oksc7oegqkl9">
          <span className="text-xs font-bold uppercase tracking-[0.14em] text-nfgreen-dark" data-id="element-481" data-mp="tjd9pg2byq60">
            What We Do
          </span>
          <h2 className="mt-3 font-display text-3xl font-extrabold text-slate-950 sm:text-4xl" data-id="element-482" data-mp="2e1nqj20cs44j">
            One team. Every irrigation need.
          </h2>
          <p className="mt-4 text-lg text-slate-600" data-id="element-483" data-mp="24bugi5fuzx2e">
            Select a service to see how Northface protects your lawn, water use, and property.
          </p>
        </div>

        <div className="mt-12 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_22px_70px_rgba(6,58,110,0.10)]" data-id="element-484" data-mp="1ue7zndf01vfs">
          <div className="grid lg:grid-cols-[0.82fr_1.18fr]" data-id="element-485" data-mp="1qbjpn2ejuih8">
            <div className="bg-slate-950 p-5 sm:p-7 lg:p-8" data-id="element-486" data-mp="1ldiagsk8v3b1">
              <div className="flex items-center justify-between border-b border-white/10 pb-5" data-id="element-487" data-mp="1le1npvyy93iq">
                <div data-id="element-488" data-mp="1gfzxmr33w3xr">
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-nfgreen-light" data-id="element-489" data-mp="1ggrsu0ssjzni">
                    Service menu
                  </p>
                  <p className="mt-1 text-sm text-slate-400" data-id="element-490" data-mp="3athoamyt5gx0">Choose what you need help with.</p>
                </div>
                <span className="font-display text-lg font-extrabold text-white" data-id="element-491" data-mp="28fjq8920041s">0{selectedService + 1}/05</span>
              </div>

              <div className="mt-4 space-y-2" role="tablist" aria-label="Irrigation services" data-id="element-492" data-mp="2ceaowkm43tr0">
                {SERVICES.map((service, index) => {
                const isSelected = selectedService === index;
                const Icon = service.icon;
                return <motion.button key={service.title} type="button" role="tab" aria-selected={isSelected} whileHover={shouldReduceMotion ? undefined : {
                  x: 3
                }} transition={{
                  duration: 0.16,
                  ease: [0.22, 1, 0.36, 1]
                }} onClick={() => setSelectedService(index)} className={`flex w-full items-center gap-4 rounded-2xl p-4 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-nfgreen focus:ring-offset-2 focus:ring-offset-slate-950 ${isSelected ? 'bg-white text-slate-950 shadow-lg' : 'text-white hover:bg-white/10'}`} data-id="element-493" data-mp="1oq9f78d767a0" data-mpi={index}>
                      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${isSelected ? 'bg-nfblue text-white' : 'bg-white/10 text-nfgreen-light'}`} data-id="element-494" data-mp="r7kk7lqre8fa">
                        <Icon className="h-5 w-5" data-id="element-495" data-mp="1158pv6pwl0c8" />
                      </span>
                      <span className="min-w-0 flex-1" data-id="element-496" data-mp="w3jc3jbaq8kg">
                        <span className={`block text-[11px] font-bold uppercase tracking-[0.12em] ${isSelected ? 'text-nfgreen-dark' : 'text-slate-400'}`} data-id="element-497" data-mp="1a4ca4e0t3lsb">
                          {service.label}
                        </span>
                        <span className="mt-1 block font-display text-base font-bold leading-tight" data-id="element-498" data-mp="1juf3ifbub1r6">
                          {service.title}
                        </span>
                      </span>
                      <ArrowRightIcon className={`h-4 w-4 shrink-0 transition-transform ${isSelected ? 'translate-x-0 text-nfblue' : '-translate-x-1 text-slate-500'}`} data-id="element-499" data-mp="1trjtkq8t3hd9" />
                    </motion.button>;
              })}
              </div>
            </div>

            <div className="relative overflow-hidden p-7 sm:p-10 lg:p-12" data-id="element-500" data-mp="1vulsdg3rgmr6">
              <div className="absolute right-0 top-0 h-52 w-52 rounded-bl-full bg-nfgreen/10" data-id="element-501" data-mp="20vt05f5wllxu" />
              <div className="relative min-h-[370px]" data-id="element-502" data-mp="25t1grbbelhwz">
                <AnimatePresence mode="wait" initial={false} data-id="element-503" data-mp="2aluqkdlqbgfx">
                  <motion.div key={activeService.title} role="tabpanel" initial={shouldReduceMotion ? {
                  opacity: 0
                } : {
                  opacity: 0,
                  y: 14
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={shouldReduceMotion ? {
                  opacity: 0
                } : {
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.24,
                  ease: [0.22, 1, 0.36, 1]
                }} data-id="element-504" data-mp="2an5rm5nsia3c">
                    <div className="flex items-start justify-between gap-5" data-id="element-505" data-mp="z6pgupn6khmt">
                      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-nfblue text-white shadow-lg shadow-nfblue/20" data-id="element-506" data-mp="rnpz0ofday5k">
                        <ActiveIcon className="h-8 w-8" data-id="element-507" data-mp="hwv1up0l2e5c" />
                      </div>
                      <span className="rounded-full border border-nfgreen/20 bg-nfgreen/10 px-3 py-1.5 text-xs font-bold text-nfgreen-dark" data-id="element-508" data-mp="hur2mkdmq3be">
                        Licensed service
                      </span>
                    </div>

                    <p className="mt-8 text-xs font-bold uppercase tracking-[0.14em] text-nfgreen-dark" data-id="element-509" data-mp="15lwhoxbqwrkn">
                      {activeService.label}
                    </p>
                    <h3 className="mt-3 max-w-xl font-display text-3xl font-extrabold leading-tight text-slate-950 sm:text-4xl" data-id="element-510" data-mp="10p5y400qfduh">
                      {activeService.title}
                    </h3>
                    <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-600" data-id="element-511" data-mp="3lb7mjmraetco">
                      {activeService.description}
                    </p>

                    <div className="mt-7 rounded-2xl border border-slate-100 bg-slate-50 p-5" data-id="element-512" data-mp="9vnp91tft8d5">
                      <p className="text-sm font-semibold leading-relaxed text-slate-700" data-id="element-513" data-mp="exwkk4ts8mif">
                        {activeService.audience}
                      </p>
                      <ul className="mt-4 grid gap-3 sm:grid-cols-3" data-id="element-514" data-mp="4xm66gw0h91o">
                        {activeService.highlights.map(item => <li key={item} className="flex items-center gap-2 text-sm font-bold text-slate-800" data-id="element-515" data-mp="snghuv4lgrkc">
                            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-nfgreen/15 text-nfgreen-dark" data-id="element-516" data-mp="xlhzrcqaaku8">
                              <CheckIcon className="h-3.5 w-3.5" data-id="element-517" data-mp="jtlm2dpd4dhd" />
                            </span>
                            {item}
                          </li>)}
                      </ul>
                    </div>

                    <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-518" data-mp="1gd2gnhh214rt">
                      <button onClick={onOpenSchedule} className="inline-flex items-center justify-center gap-2 rounded-full bg-nfblue px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-nfblue/20 transition-colors hover:bg-nfblue-dark focus:outline-none focus:ring-2 focus:ring-nfblue focus:ring-offset-2" data-id="element-519" data-mp="313pz3g66yw69">
                        Schedule a diagnostic
                        <ArrowRightIcon className="h-4 w-4" data-id="element-520" data-mp="35uy6k6nyo7gs" />
                      </button>
                      <a href={SITE.smsHref} className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold text-nfblue transition-colors hover:bg-nfblue/5 focus:outline-none focus:ring-2 focus:ring-nfblue focus:ring-offset-2" data-id="element-521" data-mp="2i5dp5s7ghi9m">
                        <MessageSquareIcon className="h-4 w-4" data-id="element-522" data-mp="2n3f729t5bbji" />
                        Ask a service question
                      </a>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}